import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { reasonList, SPAM_PILL } from '../lib/spamReasons';

// @@FORM_SPAM_v96@@ HQ › Website › Inquiries — Inbox + Spam.
// Spam = quarantined by the filter (or moved here by hand): no lead, no email.
// "Not spam" files the lead; spam is deleted automatically after 30 days.
const INTERESTS = { store: 'Store & fulfillment', production: 'Production quote', tour: 'Tour support', design: 'Graphic design', agency: 'Agency program', other: 'Something else' };
const LINK = { color: '#31b44b', textDecoration: 'none' };
const SMALL = { height: 30, padding: '0 10px', fontSize: 12, width: 'auto', marginTop: 0 };
function fmt(s) { if (!s) return '—'; try { return new Date(s).toLocaleString(); } catch { return '—'; } }

export default function WebsiteInquiries() {
  const [view, setView] = useState('inbox');
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState('');

  async function load(v = view) {
    const res = await apiFetch('/api/site/inquiries' + (v === 'spam' ? '?view=spam' : ''));
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { setMsg(d.error || 'Could not load inquiries.'); setRows([]); return; }
    setRows(d.inquiries || []); setCounts(d.counts || null);
    if (v === 'inbox' && (d.inquiries || []).some((i) => !i.seen_at)) apiFetch('/api/site/inquiries/seen', { method: 'POST' }).catch(() => {});
  }
  useEffect(() => { setRows(null); load(view); }, [view]);

  async function act(key, path, opts, done) {
    setBusy(key); setMsg('');
    const res = await apiFetch(path, opts);
    const d = await res.json().catch(() => ({}));
    setBusy('');
    if (!res.ok) { setMsg(d.error || 'That did not work.'); return; }
    if (done) setMsg(done(d));
    load();
  }
  const mark = (q, spam) => act(q.id, `/api/site/inquiries/${q.id}/spam`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ spam }),
  }, (d) => (spam
    ? `Moved to spam.${d.lead_removed ? ' The lead it created was removed too.' : (q.lead_id ? ' Its lead was kept (it has other activity).' : '')}`
    : 'Restored to the inbox and filed as a lead.'));
  const suspects = view === 'inbox' ? (rows || []).filter((q) => q.suspect && q.suspect.length) : [];

  return (
    <div className="hq-panel">
      <div className="hq-panel-head" style={{ flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', gap: 6 }} role="tablist">
          {[['inbox', 'Inbox'], ['spam', 'Spam']].map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={view === k} className={view === k ? 'hq-btn' : 'hq-signout'}
              style={{ ...SMALL, height: 34, padding: '0 14px' }} onClick={() => setView(k)}>
              {l}{counts ? ` (${k === 'inbox' ? counts.inbox : counts.spam})` : ''}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {suspects.length > 0 && (
            <button type="button" className="hq-signout" style={{ ...SMALL, height: 34, color: '#ff8c86' }} disabled={!!busy}
              onClick={() => act('bulk', '/api/site/inquiries/spam-suspected', { method: 'POST' },
                (d) => `Moved ${d.moved} to spam${d.leads_removed ? `, removed ${d.leads_removed} lead${d.leads_removed === 1 ? '' : 's'} they created` : ''}.`)}>
              Move {suspects.length} that look like spam
            </button>
          )}
          {view === 'spam' && rows && rows.length > 0 && (
            <button type="button" className="hq-signout" style={{ ...SMALL, height: 34, color: '#ff7a73' }} disabled={!!busy}
              onClick={() => { if (window.confirm(`Delete all ${rows.length} spam inquiries for good?`)) act('empty', '/api/site/inquiries/spam', { method: 'DELETE' }, (d) => `Deleted ${d.deleted}.`); }}>
              Empty spam
            </button>
          )}
        </div>
      </div>
      <p style={{ margin: '0 20px 12px', color: 'var(--hq-muted)', fontSize: 13, lineHeight: 1.5 }}>
        {view === 'inbox'
          ? 'Each inquiry is filed as a Lead (source “Website”) and emails admins and managers. Anything the spam filter catches goes to Spam instead.'
          : 'Caught by the spam filter or moved here by hand. Nothing here created a lead or sent an email. “Not spam” files the lead. Spam is deleted automatically after 30 days.'}
      </p>
      {msg && <p className="hq-msg" style={{ margin: '0 20px 12px' }}>{msg}</p>}
      <div style={{ overflowX: 'auto' }}>
        <table className="hq-table" style={{ minWidth: 820 }}>
          <thead><tr><th>Received</th><th>Name</th><th>Company</th><th>Interested in</th><th>Message</th><th>{view === 'spam' ? 'Why' : 'Lead'}</th><th /></tr></thead>
          <tbody>
            {(rows || []).map((q) => (
              <tr key={q.id}>
                <td style={{ whiteSpace: 'nowrap', color: 'var(--hq-muted)', fontSize: 13 }}>{fmt(q.created_at)}{view === 'inbox' && !q.seen_at && <span className="hq-pill hq-status-new" style={{ marginLeft: 8 }}>new</span>}</td>
                <td>{q.name}<div style={{ color: 'var(--hq-muted)', fontSize: 12 }}>{q.email}</div>
                  {q.suspect && q.suspect.length > 0 && <div style={{ marginTop: 6 }}><span style={SPAM_PILL} title={reasonList(q.suspect)}>Looks like spam</span></div>}</td>
                <td>{q.company || '—'}</td>
                <td>{INTERESTS[q.interest] || q.interest}</td>
                <td style={{ maxWidth: 360, whiteSpace: 'pre-wrap', fontSize: 13, overflowWrap: 'anywhere' }}>{q.message.length > 280 ? q.message.slice(0, 280) + '…' : q.message}</td>
                <td style={{ fontSize: 12, maxWidth: 220 }}>
                  {view === 'spam'
                    ? <span style={{ color: '#ff8c86' }}>{reasonList(q.spam_reasons) || '—'}</span>
                    : (q.lead_id ? <Link to={`/leads/${q.lead_id}`} style={LINK}>{q.lead_status || 'open'}</Link> : '—')}
                  {view === 'inbox' && q.suspect && q.suspect.length > 0 && <div style={{ color: '#ff8c86', marginTop: 4 }}>{reasonList(q.suspect)}</div>}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {view === 'inbox'
                    ? <button type="button" className="hq-signout" style={SMALL} disabled={busy === q.id} onClick={() => mark(q, true)}>Spam</button>
                    : <>
                        <button type="button" className="hq-signout" style={{ ...SMALL, color: '#31b44b' }} disabled={busy === q.id} onClick={() => mark(q, false)}>Not spam</button>{' '}
                        <button type="button" className="hq-signout" style={{ ...SMALL, color: '#ff7a73' }} disabled={busy === q.id}
                          onClick={() => act(q.id, `/api/site/inquiries/${q.id}`, { method: 'DELETE' }, () => 'Deleted.')}>Delete</button>
                      </>}
                </td>
              </tr>
            ))}
            {rows && rows.length === 0 && <tr><td className="hq-empty" colSpan={7}>{view === 'spam' ? 'Nothing in spam.' : 'No inquiries yet.'}</td></tr>}
            {rows === null && <tr><td className="hq-empty" colSpan={7}>Loading…</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
